import { useState, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog'
import { Building2, Link2, Unlink, ExternalLink, AlertCircle, Search, Plus, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { supabase } from '@/supabase-client'

/**
 * Xero Contact Linking Section Component
 *
 * Displays and manages Xero contact mappings at the CUSTOMER level.
 * One Xero contact per customer; invoices reference branches via the Reference field.
 * Inline component (no Card wrapper) similar to GoCardlessMandateSection pattern.
 *
 * Features:
 * - Search and link existing Xero contacts
 * - Create new Xero contacts from customer data
 * - View linked contact in Xero
 * - Unlink contacts with confirmation
 * - Filter out already-mapped contacts from search results
 *
 * @param {Object} props
 * @param {string|null} props.xeroContactId - Xero contact ID from customers.xero_contact_id
 * @param {string|null} props.xeroContactName - Denormalized contact name from xero_customer_mappings
 * @param {Object} props.odinCustomer - Customer object { id, name, email, phone }
 * @param {Function} props.onMappingChange - Callback to refresh parent data after mapping changes
 * @param {Function} props.onLinkSuccess - Callback after successful linking
 * @returns {JSX.Element} Inline Xero contact linking section for customer
 */
export function XeroContactLinkingSection({
  xeroContactId,
  xeroContactName,
  odinCustomer,
  onMappingChange,
  onLinkSuccess
}) {
  // Request ID ref to ignore stale async results when component remounts
  const searchRequestId = useRef(0)

  // State management — all hooks must be declared before any early return
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const [isLinking, setIsLinking] = useState(false)
  const [isUnlinking, setIsUnlinking] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [showCreateConfirm, setShowCreateConfirm] = useState(false)
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false)
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const hasContactLinked = !!xeroContactId

  // Guard: if no customer data, don't render
  if (!odinCustomer?.id) {
    return null
  }

  /**
   * Search Xero contacts and filter out already-mapped contacts
   */
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a search query')
      return
    }

    setIsSearching(true)
    const currentRequestId = ++searchRequestId.current

    try {
      // Fetch search results
      const { data: searchData, error: searchError } = await supabase.functions.invoke('xero-contacts', {
        body: { action: 'search', query: searchQuery }
      })

      if (searchError) {
        throw searchError
      }

      if (!searchData || !searchData.contacts) {
        throw new Error('Invalid response from search')
      }

      // Fetch current mappings to filter out mapped contacts
      const { data: mappingsData, error: mappingsError } = await supabase.functions.invoke('xero-contacts', {
        body: { action: 'get-mappings', includeCustomerDetails: false }
      })

      if (mappingsError) {
        // Continue without filtering if mappings fetch fails
        setSearchResults(searchData.contacts)
        return
      }

      // Build set of already-mapped Xero contact IDs
      const mappedContactIds = new Set(
        (mappingsData.mappings || []).map(m => m.xero_contact_id)
      )

      // Filter out contacts mapped to OTHER customers
      // Allow: unmapped contacts, contacts mapped to this customer
      const availableContacts = searchData.contacts.filter(contact => {
        if (!mappedContactIds.has(contact.ContactID)) return true // Not mapped at all
        // Check if mapped to current customer (allow re-selection)
        return contact.ContactID === xeroContactId
      })

      // Ignore stale results if a newer search was triggered
      if (currentRequestId !== searchRequestId.current) return

      setSearchResults(availableContacts)

      if (availableContacts.length === 0) {
        if (searchData.contacts.length === 0) {
          toast.info('No contacts found', {
            description: `No Xero contacts match "${searchQuery}". Try a different search term.`
          })
        } else {
          toast.info('No available contacts found', {
            description: `${searchData.contacts.length} contact(s) found but already mapped to other customers`
          })
        }
      }
    } catch (error) {
      console.error('[XeroContactLinkingSection] Search error:', error)

      // Handle auth errors specifically
      if (error.message?.includes('token') || error.message?.includes('auth')) {
        toast.error('Xero authentication error', {
          description: 'Please refresh the page and try again'
        })
      } else {
        toast.error('Failed to search contacts', {
          description: error.message
        })
      }
    } finally {
      setIsSearching(false)
    }
  }

  /**
   * Link selected Xero contact to Odin customer
   */
  const handleLink = async (contact) => {
    if (!odinCustomer?.id) {
      toast.error('Cannot link contact', {
        description: 'Customer must be saved first'
      })
      return
    }

    setIsLinking(true)

    try {
      const { data, error } = await supabase.functions.invoke('xero-contacts', {
        body: {
          action: 'link',
          odinCustomerId: odinCustomer.id,
          xeroContactId: contact.ContactID,
          xeroContactName: contact.Name,
          mappingType: 'manual'
        }
      })

      if (error) {
        throw error
      }

      toast.success('Contact linked successfully', {
        description: `${contact.Name} is now linked to ${odinCustomer.name}`
      })

      setIsSearchModalOpen(false)
        setSearchQuery('')
        setSearchResults([])

      if (onMappingChange) {
        onMappingChange()
      }

      if (onLinkSuccess) {
        onLinkSuccess()
      }
    } catch (error) {
      console.error('[XeroContactLinkingSection] Link error:', error)

      if (error.message?.includes('token') || error.message?.includes('auth')) {
        toast.error('Xero authentication error', {
          description: 'Please refresh the page and try again'
        })
      } else {
        toast.error('Failed to link contact', {
          description: error.message
        })
      }
    } finally {
      setIsLinking(false)
    }
  }

  /**
   * Create new Xero contact from Odin customer data - trigger confirmation dialog
   */
  const handleCreate = () => {
    if (!odinCustomer?.id) {
      toast.error('Customer ID not found')
      return
    }
    setShowCreateConfirm(true)
  }

  /**
   * Confirm and execute Xero contact creation
   */
  const confirmCreate = async () => {
    setShowCreateConfirm(false)
    setIsCreating(true)

    try {
      const { data, error} = await supabase.functions.invoke('xero-contacts', {
        body: {
          action: 'create',
          odinCustomerId: odinCustomer.id
        }
      })

      if (error) {
        console.error('[XeroContactLinkingSection] Create error:', error)

        // Try to extract detailed error message from Response object
        let errorMessage = 'Please try again'

        // Check if error.context is a Response object that needs parsing
        if (error.context instanceof Response) {
          try {
            // Clone the response so we can read it (responses can only be read once)
            const responseClone = error.context.clone()
            // Try JSON parsing first
            const errorBody = await responseClone.json()
            console.error('[XeroContactLinkingSection] Error response body:', errorBody)

            // Extract message from various possible locations
            if (errorBody.message) {
              errorMessage = errorBody.message
            } else if (errorBody.error) {
              errorMessage = errorBody.error
            } else if (errorBody.details) {
              errorMessage = errorBody.details
            }
          } catch (jsonError) {
            // If JSON parsing fails, try text
            try {
              const responseClone = error.context.clone()
              const errorText = await responseClone.text()
              console.error('[XeroContactLinkingSection] Error response text:', errorText)
              if (errorText && errorText.trim()) {
                errorMessage = errorText
              }
            } catch (textError) {
              console.error('[XeroContactLinkingSection] Could not parse error response:', textError)
            }
          }
        } else {
          // Fallback to checking other possible error locations
          if (data?.error) {
            errorMessage = data.error
          } else if (data?.message) {
            errorMessage = data.message
          } else if (error.message && !error.message.includes('non-2xx')) {
            errorMessage = error.message
          }
        }

        console.error('[XeroContactLinkingSection] Extracted error message:', errorMessage)

        // Show appropriate error based on type
        if (errorMessage.toLowerCase().includes('expired') || errorMessage.toLowerCase().includes('token')) {
          toast.error('Xero connection expired', {
            description: 'Please reconnect to Xero in Settings'
          })
        } else if (errorMessage.toLowerCase().includes('permission')) {
          toast.error('Permission denied', {
            description: 'Xero connection needs "Create Contacts" permission'
          })
        } else {
          toast.error('Failed to create Xero contact', {
            description: errorMessage
          })
        }
        return
      }

      // Handle smart-matching suggestions
      if (data?.action === 'suggest') {
        setSuggestions(data.suggestions)
        setShowSuggestions(true)
        return
      }

      toast.success('Xero contact created', {
        description: `Created contact: ${data.contact.Name}`
      })

      setIsSearchModalOpen(false)
        setSearchQuery('')
        setSearchResults([])

      if (onMappingChange) {
        onMappingChange()
      }

      if (onLinkSuccess) {
        onLinkSuccess()
      }
    } catch (error) {
      console.error('[XeroContactLinkingSection] Unexpected error:', error)
      toast.error('Failed to create Xero contact', {
        description: error.message || 'An unexpected error occurred'
      })
    } finally {
      setIsCreating(false)
    }
  }

  /**
   * Force-create a new Xero contact, bypassing smart-matching
   */
  const confirmCreateForce = async () => {
    setShowSuggestions(false)
    setSuggestions([])
    setIsCreating(true)

    try {
      const { data, error } = await supabase.functions.invoke('xero-contacts', {
        body: {
          action: 'create',
          odinCustomerId: odinCustomer.id,
          force: true
        }
      })

      if (error) throw error

      toast.success('Xero contact created', {
        description: `Created contact: ${data.contact.Name}`
      })

      setIsSearchModalOpen(false)
      setSearchQuery('')
      setSearchResults([])

      if (onMappingChange) onMappingChange()
      if (onLinkSuccess) onLinkSuccess()
    } catch (error) {
      console.error('[XeroContactLinkingSection] Force create error:', error)
      toast.error('Failed to create Xero contact', {
        description: error.message || 'An unexpected error occurred'
      })
    } finally {
      setIsCreating(false)
    }
  }

  /**
   * Link a suggested existing contact (from smart-matching)
   */
  const handleLinkSuggestion = async (contact) => {
    setShowSuggestions(false)
    setSuggestions([])
    setIsLinking(true)

    try {
      const { data, error } = await supabase.functions.invoke('xero-contacts', {
        body: {
          action: 'link',
          odinCustomerId: odinCustomer.id,
          xeroContactId: contact.ContactID,
          xeroContactName: contact.Name,
          mappingType: 'auto_matched'
        }
      })

      if (error) throw error

      toast.success('Contact linked successfully', {
        description: `Linked existing contact: ${contact.Name}`
      })

      setIsSearchModalOpen(false)
      setSearchQuery('')
      setSearchResults([])

      if (onMappingChange) onMappingChange()
      if (onLinkSuccess) onLinkSuccess()
    } catch (error) {
      console.error('[XeroContactLinkingSection] Link suggestion error:', error)
      toast.error('Failed to link contact', {
        description: error.message || 'An unexpected error occurred'
      })
    } finally {
      setIsLinking(false)
    }
  }

  /**
   * Unlink Xero contact from Odin customer - trigger confirmation dialog
   */
  const handleUnlink = () => {
    if (!odinCustomer?.id) {
      toast.error('Customer ID not found')
      return
    }
    setShowUnlinkConfirm(true)
  }

  /**
   * Confirm and execute Xero contact unlinking
   */
  const confirmUnlink = async () => {
    setShowUnlinkConfirm(false)
    setIsUnlinking(true)

    try {
      const { error } = await supabase.functions.invoke('xero-contacts', {
        body: {
          action: 'unlink',
          odinCustomerId: odinCustomer.id
        }
      })

      if (error) {
        console.error('[XeroContactLinkingSection] Unlink error:', error)

        // Extract detailed error message
        let errorMessage = 'Please try again'
        if (error.context?.body?.message) {
          errorMessage = error.context.body.message
        } else if (error.message && !error.message.includes('non-2xx')) {
          errorMessage = error.message
        }

        toast.error('Failed to unlink Xero contact', {
          description: errorMessage
        })
        return
      }

      toast.success('Xero contact unlinked')

      if (onMappingChange) {
        onMappingChange()
      }

      if (onLinkSuccess) {
        onLinkSuccess()
      }
    } catch (error) {
      console.error('[XeroContactLinkingSection] Unexpected unlink error:', error)
      toast.error('Failed to unlink Xero contact', {
        description: error.message || 'An unexpected error occurred'
      })
    } finally {
      setIsUnlinking(false)
    }
  }

  /**
   * Open search modal and initialize
   */
  const handleOpenSearch = () => {
    setIsSearchModalOpen(true)
    setSearchQuery('')
    setSearchResults([])
  }

  // Render UI based on state
  return (
    <>
      {/* State 3: Linked contact */}
      {hasContactLinked ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-[hsl(var(--odin-green))]" />
              <div>
                <p className="text-sm font-medium text-gray-900">{xeroContactName || 'Linked Contact'}</p>
                <p className="text-xs text-gray-500 font-mono">{xeroContactId}</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(`https://go.xero.com/Contacts/View/${xeroContactId}`, '_blank')}
              className="gap-2"
            >
              View in Xero
              <ExternalLink className="h-4 w-4" />
            </Button>
          </div>

          {odinCustomer?.id && (
            <div className="flex gap-2 pt-2 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenSearch}
                className="flex-1"
              >
                <Link2 className="h-4 w-4 mr-2" />
                Change Contact
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleUnlink}
                disabled={isUnlinking}
                className="flex-1"
              >
                {isUnlinking ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Unlinking...
                  </>
                ) : (
                  <>
                    <Unlink className="h-4 w-4 mr-2" />
                    Unlink
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      ) : (
        /* State 2: Not linked */
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <AlertCircle className="h-4 w-4" />
            No Xero contact linked
          </div>

          <div className="flex gap-2">
            <Button
              variant="default"
              size="sm"
              onClick={handleOpenSearch}
              className="gap-2"
            >
              <Link2 className="h-4 w-4" />
              Link Xero Contact
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCreate}
              disabled={isCreating || !odinCustomer?.id}
              className="gap-2"
            >
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  Create New Contact
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Search Modal - Always available */}
      <Dialog open={isSearchModalOpen} onOpenChange={setIsSearchModalOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Search Xero Contacts
            </DialogTitle>
            <DialogDescription>
              Search for an existing Xero contact to link to {odinCustomer?.name || 'customer'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Search Input */}
            <div className="flex gap-2">
              <Input
                placeholder="Search by name, email, or company..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch()
                  }
                }}
                disabled={isSearching || isLinking}
              />
              <Button
                onClick={handleSearch}
                disabled={isSearching || isLinking || !searchQuery.trim()}
              >
                {isSearching ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    Search
                  </>
                )}
              </Button>
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="border rounded-md divide-y max-h-[400px] overflow-y-auto">
                {searchResults.map((contact) => (
                  <div
                    key={contact.ContactID}
                    className="p-3 hover:bg-gray-50 flex items-center justify-between"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {contact.Name}
                      </p>
                      {contact.EmailAddress && (
                        <p className="text-xs text-gray-500 truncate">
                          {contact.EmailAddress}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 font-mono mt-1">
                        {contact.ContactID}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleLink(contact)}
                      disabled={isLinking}
                      className="ml-3 shrink-0"
                    >
                      {isLinking ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Linking...
                        </>
                      ) : (
                        <>
                          <Link2 className="h-4 w-4 mr-2" />
                          Link
                        </>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* No Results Message */}
            {searchResults.length === 0 && searchQuery && !isSearching && (
              <div className="text-center py-8 text-sm text-gray-500">
                <Search className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                <p>No contacts found matching "{searchQuery}"</p>
                <p className="text-xs mt-1">Try a different search term or create a new contact</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsSearchModalOpen(false)}
              disabled={isLinking}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Confirmation Dialog */}
      <AlertDialog open={showCreateConfirm} onOpenChange={setShowCreateConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Create New Xero Contact?</AlertDialogTitle>
            <AlertDialogDescription>
              This will create a new contact in Xero for <strong>{odinCustomer?.name || 'customer'}</strong> using customer information:
              <ul className="mt-2 ml-4 list-disc text-sm space-y-1">
                <li>Contact Name: {odinCustomer?.name || 'customer'}</li>
                {odinCustomer?.email && <li>Email: {odinCustomer.email}</li>}
                {odinCustomer?.phone && <li>Phone: {odinCustomer.phone}</li>}
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCreate}>
              Create Contact
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Smart-Matching Suggestions Dialog */}
      <Dialog open={showSuggestions} onOpenChange={setShowSuggestions}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Existing Contacts Found</DialogTitle>
            <DialogDescription>
              We found contacts in Xero that may match this customer. Would you like to link to an existing one?
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-64 overflow-y-auto space-y-2">
            {suggestions.map((suggestion, index) => (
              <div
                key={suggestion.contact.ContactID || index}
                className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{suggestion.contact.Name}</p>
                  {suggestion.contact.EmailAddress && (
                    <p className="text-xs text-muted-foreground truncate">{suggestion.contact.EmailAddress}</p>
                  )}
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                      suggestion.confidence >= 0.9 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                      suggestion.confidence >= 0.7 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                      'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                    }`}>
                      {Math.round(suggestion.confidence * 100)}% match
                    </span>
                    <span className="text-xs text-muted-foreground">{suggestion.matchReason}</span>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleLinkSuggestion(suggestion.contact)}
                  disabled={isLinking}
                  className="ml-2 shrink-0"
                >
                  {isLinking ? <Loader2 className="h-3 w-3 animate-spin" /> : <Link2 className="h-3 w-3" />}
                  <span className="ml-1">Use This</span>
                </Button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => { setShowSuggestions(false); setSuggestions([]) }}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={confirmCreateForce}
              disabled={isCreating}
            >
              {isCreating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Create New Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unlink Confirmation Dialog */}
      <AlertDialog open={showUnlinkConfirm} onOpenChange={setShowUnlinkConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unlink Xero Contact?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to unlink this Xero contact?
              <br />
              <strong>Note:</strong> This will not delete the contact in Xero.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmUnlink} className="bg-red-600 hover:bg-red-700">
              Unlink Contact
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

export default XeroContactLinkingSection
